import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ChevronLeft, Users, Shield, Flag, EyeOff, Send } from "lucide-react";
import { toast } from "sonner";

const BAND_LABEL = {
  VERY_CLOSE_MATCH: "Very close match",
  STRONG_MATCH: "Strong match",
  GOOD_MATCH: "Good match",
  LOW_MATCH: "Emerging match",
};
const STAGE_LABEL = {
  coursework: "Coursework", comps: "Comprehensive Exams",
  topic_development: "Topic Development", proposal: "Proposal Development",
  proposal_defense: "Proposal Defense", irb: "IRB / Ethics Approval",
  research: "Data Collection", analysis: "Data Analysis",
  dissertation: "Dissertation Writing", final_review: "Final Review",
  defense: "Dissertation Defense", revisions: "Revisions",
  graduation: "Graduation", publishing: "Publishing / Career Development",
};
const REPORT_REASONS = [
  { v: "HARASSMENT", l: "Harassment" },
  { v: "IMPERSONATION", l: "Impersonation" },
  { v: "SPAM", l: "Spam" },
  { v: "INAPPROPRIATE_CONTENT", l: "Inappropriate content" },
  { v: "OTHER", l: "Other" },
];

function ReportUserModal({ open, onClose, userId }) {
  const [reason, setReason] = useState("HARASSMENT");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/moderation/report-user", { user_id: userId, reason, description: desc });
      if (r.data.already_reported) toast.info("You already reported this profile.");
      else toast.success("Report submitted. Moderators will review.");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-6" data-testid="report-user-modal">
      <Card className="max-w-md w-full p-8">
        <h3 className="font-serif text-xl font-black tracking-tight">Report this profile</h3>
        <p className="text-xs text-muted-foreground mt-1">Confidential. Moderators review each report.</p>
        <div className="mt-6 space-y-3">
          {REPORT_REASONS.map((r) => (
            <label key={r.v} className="flex items-center gap-2 text-sm">
              <input type="radio" name="reason" value={r.v} checked={reason === r.v} onChange={() => setReason(r.v)} data-testid={`report-user-reason-${r.v}`} />
              {r.l}
            </label>
          ))}
        </div>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Add context (optional)" maxLength={1000} className="mt-4" data-testid="report-user-desc" />
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="report-user-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="report-user-submit">Submit report</Button>
        </div>
      </Card>
    </div>
  );
}

export default function ProfilePreview() {
  const { uid } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqBody, setReqBody] = useState("");

  const sendRequest = async () => {
    if (reqBody.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post("/messages/requests", { to_user_id: uid, body: reqBody });
      toast.success("Request sent. You'll see it in Messages once accepted.");
      setReqOpen(false); setReqBody("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/people/${uid}`);
      setP(r.data);
    } catch (e) {
      if (e.response?.status === 404) toast.error("Profile not available.");
      else toast.error(formatApiError(e.response?.data?.detail));
      setP({ notFound: true });
    } finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  const doBlock = async () => {
    if (!window.confirm("Block this member? You won't see each other in peer recommendations.")) return;
    setBusy(true);
    try {
      await api.post(`/people/${uid}/block`, {});
      toast.success("Member blocked.");
      nav("/people");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!p || p.notFound) {
    return (
      <div className="p-10" data-testid="profile-not-available">
        <Link to="/people" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"><ChevronLeft className="h-3 w-3" /> All people</Link>
        <p className="mt-6 font-serif text-2xl font-black tracking-tight">Profile not available.</p>
        <p className="text-sm text-muted-foreground mt-2">This member may have made their profile private, blocked you, or their account is no longer active.</p>
      </div>
    );
  }
  if (p.profile_visibility === "private") {
    return (
      <div className="p-10 max-w-2xl" data-testid="profile-private">
        <Link to="/people" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"><ChevronLeft className="h-3 w-3" /> All people</Link>
        <Card className="mt-6 p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><EyeOff className="h-3 w-3" /> Private profile</div>
          <p className="font-serif text-2xl font-black tracking-tight mt-2">{p.display_name}</p>
          <p className="text-sm text-muted-foreground mt-3">This member has kept their profile private. Only they can control what shows up here.</p>
        </Card>
      </div>
    );
  }

  const stageLabel = STAGE_LABEL[p.current_stage] || "";

  return (
    <div className="p-8 lg:p-12 max-w-4xl" data-testid="profile-preview-page">
      <Link to="/people" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"><ChevronLeft className="h-3 w-3" /> All people</Link>
      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          {p.match?.band && (
            <p className="text-[10px] uppercase tracking-widest text-primary" data-testid="profile-match-band">{BAND_LABEL[p.match.band]}</p>
          )}
          <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-2" data-testid="profile-name">{p.display_name || "Doctoral Candidate"}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {[p.degree_type, p.discipline].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {[p.methodology, p.methodology_subtype].filter(Boolean).join(" · ")}
            {stageLabel && <> · {stageLabel}</>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReqOpen(true)} disabled={busy} className="rounded-full" data-testid="profile-message-btn">
            <Send className="h-3 w-3 mr-1" /> Send message request
          </Button>
          <Button variant="outline" onClick={() => setReportOpen(true)} disabled={busy} className="rounded-full" data-testid="profile-report-btn">
            <Flag className="h-3 w-3 mr-1" /> Report
          </Button>
          <Button variant="outline" onClick={doBlock} disabled={busy} className="rounded-full" data-testid="profile-block-btn">
            <Shield className="h-3 w-3 mr-1" /> Block
          </Button>
        </div>
      </div>

      {p.match?.reasons?.length > 0 && (
        <Card className="mt-8 p-6" data-testid="profile-why-you-match">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Why you match</p>
          <div className="mt-3 space-y-2">
            {p.match.reasons.map((r, i) => (
              <p key={i} className="text-sm text-foreground/90 leading-snug">· {r}</p>
            ))}
          </div>
        </Card>
      )}

      {p.biography && (
        <Card className="mt-6 p-6" data-testid="profile-bio">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">About</p>
          <p className="text-sm mt-2 whitespace-pre-wrap leading-relaxed">{p.biography}</p>
        </Card>
      )}

      {(p.university || p.research_topic || p.dissertation_title) && (
        <Card className="mt-6 p-6" data-testid="profile-research">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Program & research</p>
          {p.university && <p className="text-sm mt-2"><span className="text-muted-foreground">University:</span> {p.university}</p>}
          {p.research_topic && <p className="text-sm"><span className="text-muted-foreground">Research topic:</span> {p.research_topic}</p>}
          {p.dissertation_title && <p className="text-sm"><span className="text-muted-foreground">Dissertation:</span> {p.dissertation_title}</p>}
          {p.expected_graduation_year && <p className="text-sm"><span className="text-muted-foreground">Expected graduation:</span> {p.expected_graduation_year}</p>}
        </Card>
      )}

      {p.research_interests?.length > 0 && (
        <Card className="mt-6 p-6" data-testid="profile-interests">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Research interests</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {p.research_interests.map((r, i) => (
              <span key={i} className="text-xs px-3 py-1 rounded-full bg-secondary">{r}</span>
            ))}
          </div>
        </Card>
      )}

      {p.shared_circles?.length > 0 && (
        <Card className="mt-6 p-6" data-testid="profile-shared-circles">
          <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /><p className="text-[10px] uppercase tracking-widest text-muted-foreground">You share {p.shared_circles.length} {p.shared_circles.length === 1 ? "Circle" : "Circles"}</p></div>
          <ul className="mt-3 space-y-1">
            {p.shared_circles.map((c) => (
              <li key={c.id}>
                <Link to={`/circles/${c.id}`} className="text-sm text-foreground hover:underline underline-offset-4">{c.name}</Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ReportUserModal open={reportOpen} onClose={() => setReportOpen(false)} userId={uid} />

      {reqOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-6" data-testid="msg-request-modal">
          <Card className="max-w-lg w-full p-8">
            <h3 className="font-serif text-xl font-black tracking-tight">Send a message request</h3>
            <p className="text-xs text-muted-foreground mt-1">This member must accept the request before a conversation opens. They see a short preview of your first message plus your privacy-projected profile.</p>
            <Textarea value={reqBody} onChange={(e) => setReqBody(e.target.value)} placeholder="Introduce yourself scholarly-style: your program, methodology, and why you'd like to connect." maxLength={5000} className="mt-4 min-h-[120px]" data-testid="msg-request-body" />
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setReqOpen(false)} className="rounded-full" data-testid="msg-request-cancel">Cancel</Button>
              <Button onClick={sendRequest} disabled={busy || reqBody.trim().length < 2} className="rounded-full" data-testid="msg-request-submit"><Send className="h-3 w-3 mr-1" /> Send request</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
